import styled from 'styled-components';
import SearchIcon from '../../../../new-components/svg-icons/SearchIcon';
import {
  useState,
  ChangeEvent,
  useEffect,
  useContext,
  useCallback
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RemoveInputIcon from '../../../../new-components/svg-icons/RemoveInputIcon';
import { SearchInputResult } from '../../../search/components/search-bar/SearchLocationResults';
import { MapMode } from '../../types';
import ResultsDropdown from './ResultsDropdown';
import { useTranslation } from 'next-i18next';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import { SearchFiltersActionTypes } from '../../../../reducers/search-filters/actions';
import { useSearch } from '../../../search/hooks/SearchInput';
import {
  setAreaLoaded,
  setDrawerOpen,
  setMapMode,
  setQuickFilter,
  setSearchDrawerOpen,
  setSelectedArea,
  setSelectedCompound
} from '../../../../redux/slices/mapSlice';
import usePolygonType from '../../hooks/PolygonTypes';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import debounce from '../../../search-new/utils';
import { updateQuery } from '../../../../helpers/common';
import { useRouter } from 'next/router';

interface SearchInputProps {
  handleRecenter: () => void;
  initialDeveloper: any;
  isSahel?: boolean;
}

const SearchInput = ({
  handleRecenter,
  initialDeveloper,
  isSahel
}: SearchInputProps) => {
  const { t } = useTranslation(['search']);
  const { getPolygonType } = usePolygonType();
  const dispatch = useDispatch();
  const router = useRouter();
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const { searchFiltersDispatch, searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const selectedArea = useSelector((state: any) => state.map.selectedArea);
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const debouncedResults = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 500),
    []
  );

  const {
    searchResults: searchLocations,
    isError,
    isLoading,
    searchTerm,
    setSearchTerm
  } = useSearch({
    searchInputLocation: 'map',
    isSahel: isSahel
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!dropdownOpen) {
      openDropdown();
    }

    if (e.target.value === '') {
      closeDropdown();
      setDebouncedTerm('');
      if (searchFiltersState.searchFilters.developers.length > 0) {
        searchFiltersDispatch({
          type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
          payload: {
            name: SearchFiltersTypes.DEVELOPERS,
            value: []
          }
        });
      }
      if (selectedCompound) {
        dispatch(setSelectedCompound(null));
        dispatch(setMapMode(MapMode.SEARCH));
      }
    }
    setDebouncedTerm(e.target.value);
  };
  const handleSelectResult = (selection: SearchInputResult) => {
    if (selection?.category === 'area') {
      if (selection?.polygon_points) {
        dispatch(
          setSelectedArea({
            id: selection.id,
            name: selection.name,
            isFiltered: false,
            geometry: {
              type: getPolygonType(selection?.polygon_points),
              coordinates: selection?.polygon_points
            },
            long: selection?.location_point?.[0],
            lat: selection?.location_point?.[1]
          })
        );
        dispatch(setMapMode(MapMode.SEARCH));
        dispatch(setSelectedCompound(null));
        dispatch(setDrawerOpen(true));
        dispatch(setSearchDrawerOpen(true));
        searchFiltersDispatch({
          type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
          payload: {
            name: SearchFiltersTypes.DEVELOPERS,
            value: []
          }
        });
        dispatch(setAreaLoaded(true));
      }
      updateQuery(
        router,
        [{ name: SearchFiltersTypes.AREAS, value: selection.id.toString() }],
        undefined,
        true
      );
      setDebouncedTerm('');
    }
    if (
      selection?.category === 'compound' ||
      selection?.category === 'new project'
    ) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.DEVELOPERS,
          value: []
        }
      });
      dispatch(
        setSelectedCompound({
          id: selection.id,
          parent_id: selection?.parent_compound_id,
          name: selection.name,
          isFiltered: false,
          geometry: {
            type: selection?.polygon_points
              ? getPolygonType(selection?.polygon_points)
              : getPolygonType(selection?.parent_polygon_points),
            coordinates:
              selection?.polygon_points ?? selection?.parent_polygon_points
          },
          long: selection?.location_point?.[0],
          lat: selection?.location_point?.[1]
        })
      );
      dispatch(setAreaLoaded(true));
      dispatch(setMapMode(MapMode.COMPOUND));
      dispatch(setDrawerOpen(true));

      setDebouncedTerm('');
    }
    if (selection?.category === 'developer') {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.DEVELOPERS,
          value: [selection.id]
        }
      });
      handleRecenter();
      dispatch(setMapMode(MapMode.SEARCH));
      dispatch(setSelectedCompound(null));
      dispatch(setDrawerOpen(false));
      setDebouncedTerm(selection.name);
      dispatch(setSearchDrawerOpen(true));
    }

    closeDropdown();
  };

  const openDropdown = useCallback(() => {
    setDropdownOpen(true);
  }, [searchTerm]);

  const closeDropdown = () => {
    if (selectedCompound && searchTerm === '') {
      setSearchTerm(selectedCompound?.name);
    }
    setDropdownOpen(false);
  };

  const removeSelection = () => {
    setSearchTerm('');
    setDebouncedTerm('');
    closeDropdown();

    if (selectedCompound) {
      dispatch(setSelectedCompound(null));
      dispatch(setMapMode(MapMode.SEARCH));
    } else if (selectedArea) {
      dispatch(setSelectedArea(null));
    }
    if (searchFiltersState.searchFilters.developers.length > 0) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.DEVELOPERS,
          value: []
        }
      });
    }
  };

  useEffect(() => {
    if (debouncedTerm || debouncedTerm === '') {
      debouncedResults(debouncedTerm);
    }
  }, [debouncedTerm, debouncedResults]);

  useEffect(() => {
    if (initialDeveloper) {
      setDebouncedTerm(initialDeveloper.name);
    }
  }, initialDeveloper);

  return (
    <Container>
      <SearchIcon fill="#212427" width="17.5" height="17.5" />

      <div className="input-section">
        <input
          value={debouncedTerm}
          type="text"
          name="search-input"
          placeholder={isMobile ? t(`search`) : t('search_by_locations')}
          onChange={e => handleInputChange(e)}
          onFocus={() => dispatch(setQuickFilter(null))}
          onClick={() => setDropdownOpen(true)}
        />

        {searchTerm !== '' && (
          <div className="close-btn" onClick={removeSelection}>
            <RemoveInputIcon />
          </div>
        )}
      </div>

      {dropdownOpen && (
        <ResultsDropdown
          isLoading={isLoading}
          isError={isError}
          searchResults={searchLocations}
          handleClick={handleSelectResult}
          closeDropdown={closeDropdown}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 360px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 360px;
  background-color: #f9fafb;
  z-index: 999;
  .input-section {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;

    input {
      width: 100%;
      font-size: 15px !important;
      font-family: materialRegular;
      background-color: #f9fafb;
      border: none;
      &:focus {
        outline: none;
      }
    }

    .close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: 100%;
    inset-inline-start: 0;
    border-radius: 10px;
    height: 45px;

    .input-section {
      input {
        font-size: 16px !important;
      }
    }
  }
`;

export default SearchInput;
